import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProductResponse } from '../../services/api.service';
import { CurrencyService } from '../../services/currency.service';
import { LanguageService } from '../../services/language.service';
import { CartItem, CartService } from '../../services/cart.service';
import { SupplementCatalogService } from '../../services/supplement-catalog.service';
import {
  SupplementTileIntent,
  productCartLines,
  routeSupplementTileIntent
} from '../../shared/supplement-tile-routing';

@Component({
    selector: 'app-product-list-item',
    templateUrl: './product-list-item.component.html',
    styleUrls: ['./product-list-item.component.scss'],
    standalone: false
})
export class ProductListItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input() product!: ProductResponse;
  @Input() currencySymbol: string = '';
  @Input() clientData: any = null;
  /**
   * The pre-add-ons badge value, still bound by the screens that host this tile.
   *
   * No longer what the badge reads. Every host computes it as `cartItems.find(i => i.id === id)`,
   * which returns whichever line happens to come first and so shows "2" to a guest holding two
   * cheese pizzas and one bacon one. The tile reads the cart itself now (`quantityInCart`), which
   * is the same number for a product without add-ons — the input stays so those templates keep
   * compiling and so a host that has not been taught about lines cannot get the badge wrong.
   */
  @Input() cartQuantity: number = 0;
  @Input() orderingEnabled: boolean = false;
  /**
   * Analytics source for the cart calls this tile makes on its own — the 1-line fast path below,
   * which no longer travels through the host's handler and so cannot inherit the host's string.
   * 'menu' is the value every host's tile handler already maps to; the menu screen overrides it
   * with 'search' while the search sheet is up, exactly as its own handlers do.
   */
  @Input() cartSource: string = 'menu';

  @Output() productClicked = new EventEmitter<number>();
  @Output() addToCartClicked = new EventEmitter<Event>();
  @Output() quantityChanged = new EventEmitter<{ event: Event; change: number }>();
  /**
   * The tile changed the cart by itself (the add-on fast path), so the host's own handler did not
   * run. Emitted with the product id purely so a host can keep bookkeeping that hangs off a stepper
   * tap — the menu's `menu_search_result_clicked` is the one that does — without the host also
   * mutating the cart and double-counting the tap.
   */
  @Output() cartAdjusted = new EventEmitter<number>();
  /**
   * The stepper routed to the picker, so the host should PRESENT the dish — nothing more.
   *
   * Separate from `productClicked` because that output means "the guest tapped this card", and the
   * menu's search list answers it by clearing `searchQuery` and tearing the result list down. That
   * is right for a card tap and wrong for a `+`: the guest would confirm their add-ons and land on a
   * blank search screen, having to retype to add a second dish — where the same `+` on a dish
   * WITHOUT add-ons leaves the results untouched. iOS presents the sheet over the search results for
   * the same reason.
   */
  @Output() sheetRequested = new EventEmitter<number>();

  loadedImages = new Set<number>();
  errorImages = new Set<number>();

  /**
   * This product's cart lines, in the one deterministic order every add-on surface uses.
   *
   * A product id no longer identifies a row: one dish can sit in the order under several add-on
   * selections. The tile needs the whole set both to size its badge and to decide whether `+` can
   * act on its own or has to ask.
   */
  cartLines: CartItem[] = [];

  /**
   * What the badge shows: the SUM across those lines. A guest with two cheese pizzas and one bacon
   * pizza has ordered three of that dish.
   */
  quantityInCart = 0;

  private cartItems: CartItem[] = [];
  private cartSub?: Subscription;

  constructor(
    private currencyService: CurrencyService,
    private languageService: LanguageService,
    private cartService: CartService,
    private supplementCatalog: SupplementCatalogService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.preloadImage();
    this.cartSub = this.cartService.cartItems$.subscribe(items => {
      this.cartItems = items;
      this.refreshCartState();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-validate when the product (and thus its image URL) changes.
    if (changes['product'] && !changes['product'].firstChange) {
      this.errorImages.clear();
      this.loadedImages.clear();
      this.preloadImage();
      // The tile is recycled across products by *ngFor, so the lines cached for the previous one
      // would otherwise drive this one's badge until the cart next emitted.
      this.refreshCartState();
    }
  }

  ngOnDestroy(): void {
    this.cartSub?.unsubscribe();
  }

  // Proactively load the image URL so we can reliably detect a broken/404 src.
  // SVG <image> does not fire (error) consistently across browsers, so we probe
  // with a real Image() and hide the tile on failure (iOS parity: no tile when
  // there is no usable image).
  private preloadImage(): void {
    if (!this.hasValidImage()) {
      return;
    }
    const src = this.product.images[0].src;
    const img = new Image();
    img.onload = () => this.ngZone.run(() => { this.onImageLoad(); this.cdr.markForCheck(); });
    img.onerror = () => this.ngZone.run(() => { this.onImageError(); this.cdr.markForCheck(); });
    img.src = src;
  }

  private refreshCartState(): void {
    this.cartLines = productCartLines(this.cartItems, this.product?.id ?? -1);
    this.quantityInCart = this.cartLines.reduce((sum, line) => sum + line.quantity, 0);
  }

  get isInCart(): boolean {
    return this.quantityInCart > 0;
  }

  /**
   * How many distinct add-on selections of this dish are in the order. Only ever above 1 for a
   * product with add-ons, so no catalogue lookup is needed to decide whether to draw the chip.
   */
  get combinationCount(): number {
    return this.cartLines.length;
  }

  onProductClick(): void {
    this.productClicked.emit(this.product.id);
  }

  onAddToCart(event: Event): void {
    event.stopPropagation();
    this.runTileIntent('increment', () => this.addToCartClicked.emit(event));
  }

  onQuantityChange(event: Event, change: number): void {
    event.stopPropagation();
    this.runTileIntent(
      change > 0 ? 'increment' : 'decrement',
      () => this.quantityChanged.emit({ event, change })
    );
  }

  /** The chip opens the same picker `+` does — "which of these did you mean" is the same question. */
  onCombinationsTap(event: Event): void {
    event.stopPropagation();
    this.openSheet();
  }

  /**
   * The 0 / 1 / 2+ branch, decided by the shared router so this tile, the featured strip and any
   * later surface cannot drift apart on it.
   */
  private runTileIntent(intent: SupplementTileIntent, fallback: () => void): void {
    const action = routeSupplementTileIntent(
      intent,
      this.supplementCatalog.productHasSupplements(this.product),
      this.cartLines
    );

    switch (action.kind) {
      case 'fallback':
        // No add-ons: the host's handler runs untouched, analytics source strings and all. This is
        // what makes the whole change a no-op for venues that never enabled the feature.
        fallback();
        return;

      case 'openSheet':
        this.openSheet();
        return;

      case 'adjustLine': {
        const lineKey = this.cartService.getLineKey(action.line);
        if (action.delta === 'reset') {
          this.cartService.removeLine(lineKey, this.cartSource);
        } else {
          // `updateLineQuantity` re-applies the availability guard when the number goes UP and
          // routes to `removeLine` when it reaches zero, so skipping the sheet here is not a way
          // around either — which is why the fast path needs no gate of its own.
          this.cartService.updateLineQuantity(
            lineKey,
            action.line.quantity + action.delta,
            this.cartSource
          );
        }
        this.cartAdjusted.emit(this.product.id);
        return;
      }

      case 'none':
        // The stepper is not rendered at zero, so a `−` that lands here has no line to act on.
        return;
    }
  }

  /**
   * Through the host's own product-open path rather than ProductSheetService directly: that is
   * where `product_open` is logged with the right entry screen. A tile only ever opens the sheet in
   * ADD mode — pointing at one existing line is what the sheet's own "in your order" list is for.
   */
  private openSheet(): void {
    this.sheetRequested.emit(this.product.id);
  }

  getProductName(): string {
    return this.languageService.localizeMetaValue(this.product.meta_data, '', this.product.name);
  }

  getProductDescription(): string {
    return this.languageService.localizeMetaValue(this.product.meta_data, 'description', this.product.description);
  }

  hasValidImage(): boolean {
    return !!(this.product.images && this.product.images.length > 0 && this.product.images[0] && this.product.images[0].src);
  }

  getProductImageSrc(): string {
    return this.hasValidImage() ? this.product.images[0].src : '../../../assets/ImageBroken.svg';
  }

  onImageLoad(): void { this.loadedImages.add(this.product.id); }
  onImageError(): void { this.errorImages.add(this.product.id); }
  isImageLoaded(): boolean { return this.loadedImages.has(this.product.id); }
  isImageError(): boolean { return this.errorImages.has(this.product.id); }

  getSecondPrice(price: string | number): string | null {
    return this.currencyService.getSecondPrice(price, this.clientData);
  }

  getSecondCurrencySymbol(): string | null {
    return this.currencyService.getSecondCurrencySymbol(this.clientData);
  }

  isSecondCurrencyActive(): boolean {
    return this.currencyService.isSecondCurrencyActive(this.clientData);
  }
}
